import React, { useState, useEffect, useContext } from 'react';
import { AppContext } from '../context/AppContext';
import { useTranslation } from 'react-i18next';

const ExpiryAlerts = () => {
    const { authFetch } = useContext(AppContext);
    const { t } = useTranslation();
    const [items, setItems] = useState([]);
    const [days, setDays] = useState(60);
    const [loading, setLoading] = useState(false);

    const fetchExpiry = (d) => {
        setLoading(true);
        authFetch(`/api/reports?type=expiry&days=${d}`)
            .then(r => r.json())
            .then(data => { setItems(Array.isArray(data) ? data : []); setLoading(false); })
            .catch(() => setLoading(false));
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { fetchExpiry(days); }, [days]);

    const getUrgency = (daysLeft) => {
        if (daysLeft <= 0) return { label: t('EXPIRED'), bg: 'bg-red-100', border: 'border-red-400', text: 'text-red-800', badge: 'bg-red-500' };
        if (daysLeft <= 7) return { label: t('Critical'), bg: 'bg-red-50', border: 'border-red-300', text: 'text-red-700', badge: 'bg-red-400' };
        if (daysLeft <= 30) return { label: t('Expiring Soon'), bg: 'bg-orange-50', border: 'border-orange-300', text: 'text-orange-700', badge: 'bg-orange-400' };
        return { label: t('Watch'), bg: 'bg-yellow-50', border: 'border-yellow-300', text: 'text-yellow-700', badge: 'bg-yellow-400' };
    };

    const counts = {
        expired: items.filter(i => parseInt(i.days_until_expiry) <= 0).length,
        critical: items.filter(i => parseInt(i.days_until_expiry) > 0 && parseInt(i.days_until_expiry) <= 7).length,
        soon: items.filter(i => parseInt(i.days_until_expiry) > 7 && parseInt(i.days_until_expiry) <= 30).length,
        watch: items.filter(i => parseInt(i.days_until_expiry) > 30).length,
    };

    return (
        <div>
            <div className="flex justify-between items-center mb-6">
                <div>
                    <h2 className="text-3xl font-bold text-gray-800">{t('⚠️ Expiry Alerts')}</h2>
                    <p className="text-gray-500 text-sm mt-1">{t('Products approaching their expiry date. Act now to minimize waste.')}</p>
                </div>
                <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-600">{t('Show next')}</span>
                    <select className="border rounded-lg p-2 shadow-sm" value={days} onChange={e => setDays(e.target.value)}>
                        <option value="7">{t('7 days')}</option>
                        <option value="30">{t('30 days')}</option>
                        <option value="60">{t('60 days')}</option>
                        <option value="90">{t('90 days')}</option>
                    </select>
                </div>
            </div>

            {/* Alert Summary Chips */}
            <div className="flex gap-4 mb-8 flex-wrap">
                {counts.expired > 0 && <div className="bg-red-500 text-white px-4 py-2 rounded-full font-bold shadow">🚨 {counts.expired} {t('Expired (<30d)')}</div>}
                {counts.critical > 0 && <div className="bg-red-400 text-white px-4 py-2 rounded-full font-bold shadow">🔴 {counts.critical} {t('Critical (<7d)')}</div>}
                {counts.soon > 0 && <div className="bg-orange-400 text-white px-4 py-2 rounded-full font-bold shadow">🟠 {counts.soon} {t('Expiring Soon (<30d)')}</div>}
                {counts.watch > 0 && <div className="bg-yellow-400 text-white px-4 py-2 rounded-full font-bold shadow">🟡 {counts.watch} {t('Watch (<{{days}}d)', { days })}</div>}
                {items.length === 0 && !loading && <div className="bg-emerald-100 text-emerald-800 px-4 py-2 rounded-full font-semibold">✅ {t('No expirations within')} {days} {t('days')}</div>}
            </div>

            {loading && <div className="text-center py-12 text-gray-400">{t('Scanning inventory...')}</div>}

            <div className="space-y-3">
                {items.map(item => {
                    const daysLeft = parseInt(item.days_until_expiry);
                    const u = getUrgency(daysLeft);
                    return (
                        <div key={item.id} className={`flex items-center justify-between p-4 rounded-xl border-2 ${u.bg} ${u.border}`}>
                            <div className="flex items-center gap-4">
                                <span className={`${u.badge} text-white text-xs font-bold px-2 py-1 rounded-full whitespace-nowrap`}>
                                    {daysLeft <= 0 ? t('EXPIRED') : `${daysLeft}${t('d left')}`}
                                </span>
                                <div>
                                    <p className={`font-bold ${u.text}`}>{item.product_name}</p>
                                    <p className="text-xs text-gray-500">
                                        {item.depo_name} · {t('Reference Number')}: {item.sku || '—'} · {t('Barcode')}: {item.barcode || '—'}
                                    </p>
                                </div>
                            </div>
                            <div className="text-right">
                                <p className={`font-bold ${u.text}`}>{item.quantity} {item.unit || t('units')} {t('in stock')}</p>
                                <p className="text-xs text-gray-500">{t('Expires:')} {new Date(item.expiry_date).toLocaleDateString()}</p>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default ExpiryAlerts;
